import React, { useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { useProjectStore } from '../../hooks/useProjectStore';
import { fractureAPI } from '../../services/api';
import type { StageResult } from './types';

// /fracsim/stage — 单段缝面细节 (三级: 选段看单条剖面)
const TH = {
  bg: '#FFFFFF', canvasBg: '#FFFFFF', grid: 'rgba(30,42,58,0.08)',
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  accent: '#C2533A', headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9',
  w: '#2874A6', pn: '#6A4C93',
  fontUI: '"Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};
const effColor = (e: number) => e >= 60 ? '#1E8449' : e >= 40 ? '#2874A6' : e >= 25 ? '#D4770C' : '#C0392B';

type Mesh = { nx: number; x: number[]; h: number[]; w0_mm: number[]; pn_mpa: number[] };
type StageRes = {
  stage_number: number; perf_md: number; half_length: number; propped_half_length: number;
  height: number; w0_mm: number; net_pressure_mpa: number; efficiency_pct: number;
  conductivity: number; mesh?: Mesh;
};

type Props = {
  embedded?: boolean;
  stageResults?: StageResult[];
  selectedStage?: number | null;
  onSelectStage?: (stageNumber: number) => void;
  onClose?: () => void;
};

export default function FracStageDetailPage({
  embedded = false,
  stageResults,
  selectedStage,
  onSelectStage,
  onClose,
}: Props = {}) {
  const { currentProject } = useProjectStore();
  const [stages, setStages] = useState<StageRes[]>([]);
  const [sel, setSel] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const un = (d: any) => (Array.isArray(d) ? d : d?.results ?? []);

  useEffect(() => {
    if (stageResults) {
      const rs: StageRes[] = stageResults.slice()
        .sort((a, b) => a.stage_number - b.stage_number)
        .map((stage) => ({
          stage_number: stage.stage_number,
          perf_md: stage.perf_md,
          half_length: stage.half_length,
          propped_half_length: stage.propped_half_length,
          height: stage.height,
          w0_mm: stage.w0_mm,
          net_pressure_mpa: stage.net_pressure_mpa,
          efficiency_pct: stage.efficiency_pct,
          conductivity: stage.conductivity,
          mesh: stage.mesh as unknown as Mesh | undefined,
        }));
      setStages(rs);
      const preferred = selectedStage != null && rs.some((stage) => stage.stage_number === selectedStage)
        ? selectedStage : rs[0]?.stage_number ?? null;
      setSel(preferred);
      setLoading(false);
      return;
    }
    if (!currentProject) { setLoading(false); return; }
    setLoading(true);
    fractureAPI.listSimulations({ project: currentProject.id }).then(({ data }) => {
      const sims = un(data);
      const latest = sims.length ? sims.slice().sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0] : null;
      const rs: StageRes[] = (latest?.results ?? []).slice().sort((a: StageRes, b: StageRes) => a.stage_number - b.stage_number);
      setStages(rs);
      if (rs.length) setSel(rs[0].stage_number);
    }).catch((e) => message.error(e?.message || '加载失败')).finally(() => setLoading(false));
  }, [currentProject, selectedStage, stageResults]);

  const stage = stages.find((s) => s.stage_number === sel);
  if (!currentProject) return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;

  return (
    <div style={{ height: embedded ? '100%' : '100vh', display: 'flex', flexDirection: 'column', background: TH.bg, fontFamily: 'inherit' }}>
      <div style={{ padding: '8px 20px', borderBottom: `1px solid ${TH.headerBorder}`, display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: TH.text, margin: 0 }}>单段分析</h2>
        {stages.length > 0 && (
          <select value={sel ?? ''} onChange={(e) => {
            const stageNumber = Number(e.target.value);
            setSel(stageNumber);
            onSelectStage?.(stageNumber);
          }}
            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 4, border: `1px solid ${TH.headerBorder}` }}>
            {stages.map((s) => <option key={s.stage_number} value={s.stage_number}>第 {s.stage_number} 段</option>)}
          </select>
        )}
        <span style={{ fontSize: 12, color: TH.textMuted }}>{loading ? '加载中…' : `${embedded ? '当前模拟' : '取最新一次模拟'} · 共 ${stages.length} 段`}</span>
        {embedded && <button onClick={onClose} style={{ marginLeft: 'auto', padding: '3px 8px', border: `1px solid ${TH.headerBorder}`, background: '#fff', color: TH.textSec, borderRadius: 3, cursor: 'pointer' }}>收起</button>}
      </div>

      {!stage ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: TH.textMuted }}>
          {loading ? '' : '暂无模拟结果 — 先到「裂缝扩展模拟」运行模型'}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 1, background: TH.headerBorder }}>
            {[
              ['半缝长', `${stage.half_length} m`], ['支撑半缝长', `${stage.propped_half_length} m`],
              ['缝高', `${stage.height} m`], ['井底缝宽', `${stage.w0_mm} mm`],
              ['净压', `${stage.net_pressure_mpa} MPa`], ['液效', `${stage.efficiency_pct}%`],
              ['导流', `${stage.conductivity} D·mm`],
            ].map(([l, v], i) => (
              <div key={i} style={{ flex: 1, padding: '6px 8px', textAlign: 'center', background: '#fff' }}>
                <div style={{ fontSize: 9, color: TH.textMuted }}>{l}</div>
                <div style={{ fontSize: 14, fontWeight: 700, fontFamily: TH.fontMono, color: i === 5 ? effColor(stage.efficiency_pct) : TH.text }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, minHeight: 0, background: TH.headerBorder }}>
            <Panel title="缝面轮廓 (长 × 高, 椭圆)"><SectionView stage={stage} /></Panel>
            <Panel title="沿缝长剖面 (缝宽 / 净压, Nordgren 衰减)"><ProfileView stage={stage} /></Panel>
          </div>
        </>
      )}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: TH.canvasBg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, color: TH.accent, background: TH.panelHeaderBg, borderBottom: `1px solid ${TH.headerBorder}` }}>{title}</div>
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
}

// 缝面椭圆轮廓 (半缝长 × 缝高) + 支撑范围
function SectionView({ stage }: { stage: StageRes }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 2;
    const rect = cv.getBoundingClientRect();
    cv.width = rect.width * dpr; cv.height = rect.height * dpr; ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    ctx.fillStyle = TH.canvasBg; ctx.fillRect(0, 0, W, H);
    const L = stage.half_length || 1, Hh = stage.height || 1;
    const padX = 30, padY = 30;
    const cx = W / 2, cy = H / 2;
    const sx = (W - 2 * padX) / (2 * L), sy = (H - 2 * padY) / Hh;
    const col = effColor(stage.efficiency_pct);
    // 缝网椭圆
    ctx.fillStyle = col + '33'; ctx.strokeStyle = col; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.ellipse(cx, cy, L * sx, (Hh / 2) * sy, 0, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    // 支撑范围
    ctx.fillStyle = col + 'AA';
    ctx.beginPath(); ctx.ellipse(cx, cy, stage.propped_half_length * sx, (Hh / 2) * sy * 0.85, 0, 0, Math.PI * 2); ctx.fill();
    // 井筒
    ctx.fillStyle = TH.text; ctx.beginPath(); ctx.arc(cx, cy, 3, 0, Math.PI * 2); ctx.fill();
    // 标注
    ctx.fillStyle = TH.textSec; ctx.font = `10px ${TH.fontUI}`; ctx.textAlign = 'center';
    ctx.fillText(`← 半缝长 ${stage.half_length} m →`, cx, H - 10);
    ctx.save(); ctx.translate(14, cy); ctx.rotate(-Math.PI / 2); ctx.fillText(`缝高 ${stage.height} m`, 0, 0); ctx.restore();
  }, [stage]);
  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
}

// 沿缝长的缝宽 / 净压剖面 (优先用 mesh, 没有则用 Nordgren 解析衰减)
function ProfileView({ stage }: { stage: StageRes }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 2;
    const rect = cv.getBoundingClientRect();
    cv.width = rect.width * dpr; cv.height = rect.height * dpr; ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    ctx.fillStyle = TH.canvasBg; ctx.fillRect(0, 0, W, H);
    const L = stage.half_length || 1;
    const padL = 44, padR = 44, padT = 18, padB = 28;
    const plotW = W - padL - padR, plotH = H - padT - padB;
    // 数据: mesh 优先
    let xs: number[], ws: number[], ps: number[];
    if (stage.mesh?.x?.length) {
      xs = stage.mesh.x; ws = stage.mesh.w0_mm; ps = stage.mesh.pn_mpa;
    } else {
      const n = 30; xs = []; ws = []; ps = [];
      for (let i = 0; i < n; i++) { const x = (L * i) / (n - 1); const d = Math.pow(Math.max(0, 1 - x / L), 0.25); xs.push(x); ws.push(stage.w0_mm * d); ps.push(stage.net_pressure_mpa * d); }
    }
    const wMax = Math.max(...ws, 0.01) * 1.15, pMax = Math.max(...ps, 0.01) * 1.15;
    const X = (x: number) => padL + (x / L) * plotW;
    const Yw = (v: number) => padT + (1 - v / wMax) * plotH;
    const Yp = (v: number) => padT + (1 - v / pMax) * plotH;
    ctx.strokeStyle = TH.grid; ctx.lineWidth = 0.5; ctx.font = `9px ${TH.fontMono}`;
    for (let k = 0; k <= 4; k++) {
      const y = padT + (k / 4) * plotH;
      ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(W - padR, y); ctx.stroke();
      ctx.fillStyle = TH.w; ctx.textAlign = 'right'; ctx.fillText((wMax * (1 - k / 4)).toFixed(2), padL - 4, y + 3);
      ctx.fillStyle = TH.pn; ctx.textAlign = 'left'; ctx.fillText((pMax * (1 - k / 4)).toFixed(1), W - padR + 4, y + 3);
    }
    // 缝宽
    ctx.strokeStyle = TH.w; ctx.lineWidth = 2; ctx.beginPath();
    xs.forEach((x, i) => { const px = X(x), py = Yw(ws[i]); i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py); }); ctx.stroke();
    // 净压
    ctx.strokeStyle = TH.pn; ctx.lineWidth = 1.5; ctx.setLineDash([4, 3]); ctx.beginPath();
    xs.forEach((x, i) => { const px = X(x), py = Yp(ps[i]); i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py); }); ctx.stroke(); ctx.setLineDash([]);
    // 图例 + 轴
    ctx.font = `9px ${TH.fontUI}`; ctx.textAlign = 'left';
    ctx.fillStyle = TH.w; ctx.fillText('缝宽 mm', padL, padT - 4);
    ctx.fillStyle = TH.pn; ctx.textAlign = 'right'; ctx.fillText('净压 MPa', W - padR, padT - 4);
    ctx.fillStyle = TH.textMuted; ctx.textAlign = 'center'; ctx.fillText('井底 → 缝尖 (m)', padL + plotW / 2, H - 8);
  }, [stage]);
  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
}
